import { z } from 'zod';
import { uuidSchema } from './common.schema.js';

// ---------------------------------------------------------------------------
// Enum constants
// ---------------------------------------------------------------------------

export const INVENTORY_LOT_STATUSES = ['AVAILABLE', 'QUARANTINED', 'EXPIRED', 'RECALLED'] as const;
export const INVENTORY_UNIT_STATUSES = ['SEALED', 'OPENED', 'IN_USE', 'CONSUMED', 'EXPIRED', 'DISPOSED', 'QUARANTINED', 'RECALLED'] as const;

/**
 * expiry_date accepts either a bare calendar date (YYYY-MM-DD) or a full UTC ISO
 * datetime — the frontend sends a UTC instant (converted from the user's local
 * picker selection), which the service resolves down to the intended calendar
 * date. Bare-date is still accepted for backward compatibility with any other caller.
 */
const expiryDateInputSchema = z
  .string()
  .refine(
    (v) => /^\d{4}-\d{2}-\d{2}$/.test(v) || !isNaN(Date.parse(v)),
    'expiry_date must be YYYY-MM-DD or a valid ISO datetime',
  );

// ---------------------------------------------------------------------------
// Create Inventory Receipt request schema
// Inserts one row into `inventory` and N rows into `inventory_units`
// where N = quantity (number of units/boxes received)
// ---------------------------------------------------------------------------

export const CreateInventoryReceiptSchema = z.object({
  /** Hospital scoping the receipt */
  hospital_id: uuidSchema,
  /** Product being received */
  product_id: uuidSchema,
  /** Storage location ID */
  location_id: uuidSchema,
  /**
   * Lot / batch number.
   * If omitted, the service auto-generates one as "PB-HMF-YYYY-NNN".
   */
  batch_number: z.string().max(100).optional(),
  /** Expiry datetime in ISO 8601 format with UTC offset — required on receipt */
  expiry_date: z
    .string()
    .datetime({ offset: true }),
  /** Number of units/boxes received — also the number of inventory_units rows created */
  quantity: z.coerce.number().int().positive(),
  /** Lot availability status */
  lot_status: z.enum(INVENTORY_LOT_STATUSES).optional().default('AVAILABLE'),
  /** Staff member who received the stock */
  received_by: uuidSchema.optional().nullable(),
  /** Whether the packaging already carries a 2-D barcode */
  existing_2d_barcode: z.boolean().optional().default(false),
  per_unit_quantity: z.coerce.number().positive(),
  /** Product SKU, also used as the tms_uuid prefix (e.g. "NAN-PRE") */
  sku: z.string().max(100),
  /** Unit of measure for each individual unit/box (e.g. "g", "ml") */
  unit: z.string().max(20).optional().default(''),
  /**
   * Pre-printed 2-D barcode values — one per unit.
   * Required when `existing_2d_barcode` is true.
   * Length must equal `quantity` (one barcode per unit/box).
   */
  barcodes: z.array(z.string().min(1)).optional(),
}).superRefine((data, ctx) => {
  if (data.existing_2d_barcode) {
    const unitCount = data.quantity;
    if (!data.barcodes || data.barcodes.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'barcodes array is required when existing_2d_barcode is true',
        path: ['barcodes'],
      });
    } else if (data.barcodes.length !== unitCount) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `barcodes must contain exactly ${unitCount} entries (one per unit, equal to quantity)`,
        path: ['barcodes'],
      });
    }
  }
});

export type CreateInventoryReceipt = z.infer<typeof CreateInventoryReceiptSchema>;

// ---------------------------------------------------------------------------
// Response schemas
// ---------------------------------------------------------------------------

export const InventoryResponseSchema = z.object({
  inventory_id: uuidSchema,
  hospital_id: uuidSchema,
  product_id: uuidSchema,
  location_id: uuidSchema,
  batch_number: z.string(),
  expiry_date: z.string().nullable(),
  quantity: z.string(),
  lot_status: z.string(),
  received_by: uuidSchema.nullable(),
  received_at: z.string().nullable(),
  existing_2d_barcode: z.boolean(),
  updated_at: z.string(),
  /** Number of inventory_units rows created */
  units_created: z.number(),
});

export type InventoryResponse = z.infer<typeof InventoryResponseSchema>;

// ---------------------------------------------------------------------------
// GET /inventory — list by hospital + product
// ---------------------------------------------------------------------------

export const InventoryListItemSchema = z.object({
  inventory_id: uuidSchema,
  hospital_id: uuidSchema,
  product_id: uuidSchema,
  location_id: uuidSchema,
  location_name: z.string(),
  batch_number: z.string(),
  expiry_date: z.string().nullable(),
  quantity: z.string(),
  lot_status: z.string(),
  existing_2d_barcode: z.boolean(),
  // ── Catalog product fields (present when a catalog copy exists for this hospital) ──
  product_name: z.string().nullable().optional(),
  sku: z.string().nullable().optional(),
  brand_name: z.string().nullable().optional(),
  unit_of_measure: z.string().nullable().optional(),
  image_url: z.string().nullable().optional(),
});

export type InventoryListItem = z.infer<typeof InventoryListItemSchema>;

// ---------------------------------------------------------------------------
// PUT /inventory/:inventory_id — update a lot
// ---------------------------------------------------------------------------

export const UpdateInventorySchema = z.object({
  /** Storage location ID */
  location_id: uuidSchema.optional(),
  /** Expiry datetime in ISO 8601 format with UTC offset */
  expiry_date: z
    .string()
    .datetime({ offset: true })
    .optional()
    .nullable(),
  /** Number of units/boxes (updates the lot quantity field) */
  quantity: z.coerce.number().int().min(0).optional(),
  /** Lot availability status */
  lot_status: z.enum(INVENTORY_LOT_STATUSES).optional(),
  /** Staff member */
  received_by: uuidSchema.optional().nullable(),
  /** Whether the packaging carries a 2-D barcode */
  existing_2d_barcode: z.boolean().optional(),
  /** Quantity per individual unit/box — used for new units added */
  per_unit_quantity: z.coerce.number().positive().optional(),
  /** Unit of measure */
  unit: z.string().max(20).optional(),
  /**
   * SKU prefix for auto-generating tms_uuid values when existing_2d_barcode=false.
   * If omitted the service looks up the SKU from existing units of this lot.
   */
  sku: z.string().max(100).optional(),
  /**
   * Barcodes to ADD as new inventory_units rows.
   * - existing_2d_barcode=true: each value is used directly as tms_uuid.
   * - existing_2d_barcode=false: length drives how many units to auto-generate; values are ignored.
   */
  add_barcodes: z.array(z.string().min(1)).optional(),
  /**
   * tms_uuid values of inventory_units rows to REMOVE (delete).
   */
  remove_barcodes: z.array(z.string().min(1)).optional(),
});

export type UpdateInventory = z.infer<typeof UpdateInventorySchema>;

export const UpdateInventoryResponseSchema = z.object({
  inventory_id: uuidSchema,
  hospital_id: uuidSchema,
  product_id: uuidSchema,
  location_id: uuidSchema,
  batch_number: z.string(),
  expiry_date: z.string().nullable(),
  quantity: z.string(),
  lot_status: z.string(),
  received_by: uuidSchema.nullable(),
  existing_2d_barcode: z.boolean(),
  updated_at: z.string(),
  units_added: z.number(),
  units_removed: z.number(),
});

export type UpdateInventoryResponse = z.infer<typeof UpdateInventoryResponseSchema>;

// ---------------------------------------------------------------------------
// Delete Inventory response schema
// ---------------------------------------------------------------------------


export const DeleteInventoryResponseSchema = z.object({
  inventory_id:        z.string(),
  lot_status:          z.enum(['DELETED']),
  batch_status:        z.enum(['DELETED']),
  units_deleted:       z.number(),
  transactions_deleted: z.number(),
});

export type DeleteInventoryResponse = z.infer<typeof DeleteInventoryResponseSchema>;

// ---------------------------------------------------------------------------
// DELETE /inventory/product/:product_id/permanent — permanent hard delete by product
// ---------------------------------------------------------------------------

export const PermanentDeleteByProductResponseSchema = z.object({
  product_id:           uuidSchema,
  hospital_id:          uuidSchema,
  lots_deleted:         z.number().int().nonnegative(),
  units_deleted:        z.number().int().nonnegative(),
  transactions_deleted: z.number().int().nonnegative(),
  batches_deleted:      z.number().int().nonnegative(),
});

export type PermanentDeleteByProductResponse = z.infer<typeof PermanentDeleteByProductResponseSchema>;

// ---------------------------------------------------------------------------
// GET /inventory/summary — one row per product, total quantity
// ---------------------------------------------------------------------------

export const InventoryProductSummarySchema = z.object({
  hospital_id:     uuidSchema,
  product_id:      uuidSchema,
  total_quantity:  z.number(),
  product_name:    z.string().nullable(),
  display_name:    z.string().nullable(),
  sku:             z.string().nullable(),
  brand_name:      z.string().nullable(),
  form_name:       z.string().nullable(),
  unit_of_measure: z.string().nullable(),
  image_url:       z.string().nullable(),
});

export type InventoryProductSummary = z.infer<typeof InventoryProductSummarySchema>;

// ---------------------------------------------------------------------------
// GET /inventory/:inventory_id/units — list inventory_units for a lot
// ---------------------------------------------------------------------------

export const InventoryUnitItemSchema = z.object({
  unit_id:            uuidSchema,
  batch_number:       z.string(),
  sku:                z.string(),
  tms_uuid:           z.string(),
  lot_number:         z.string().nullable(),
  expiry_date:        z.string(),
  // Unit's own current storage location — may differ from the lot's `inventory.location_id`
  // once a unit has been restored to a different location (e.g. via Feed Prep's "Restore To").
  location_id:        uuidSchema,
  original_quantity:  z.string(),
  remaining_quantity: z.string(),
  unit:               z.string(),
  unit_status:        z.string(),
  label_generated:    z.boolean(),
  received_at:        z.string(),
  received_by:        uuidSchema.nullable(),
  notes:              z.string().nullable(),
  created_at:         z.string(),
  updated_at:         z.string(),
});

export type InventoryUnitItem = z.infer<typeof InventoryUnitItemSchema>;

export const InventoryWithUnitsSchema = z.object({
  inventory: z.object({
    inventory_id:        uuidSchema,
    hospital_id:         uuidSchema,
    product_id:          uuidSchema,
    location_id:         uuidSchema,
    batch_number:        z.string(),
    expiry_date:         z.string().nullable(),
    quantity:            z.string(),
    reserved:            z.string(),
    unit:                z.string(),
    lot_status:          z.string(),
    received_by:         uuidSchema.nullable(),
    received_at:         z.string().nullable(),
    existing_2d_barcode: z.boolean(),
    updated_at:          z.string(),
  }),
  units: z.array(InventoryUnitItemSchema),
});

export type InventoryWithUnits = z.infer<typeof InventoryWithUnitsSchema>;

// ---------------------------------------------------------------------------
// DELETE /inventory/:inventory_id/units — bulk soft-delete units (DISPOSED)
// ---------------------------------------------------------------------------

export const DeleteUnitsRequestSchema = z.object({
  /** Hospital scoping the units */
  hospital_id:  uuidSchema,
  /**
   * Human-readable unit identifiers (tms_uuid) to soft-delete.
   * All must belong to the lot identified by the :inventory_id route param.
   * Accepted unit statuses: SEALED, OPENED, CONSUMED.
   */
  tms_uuids:    z.array(z.string().min(1)).min(1, 'tms_uuids must contain at least one entry'),
  /** Staff member performing the disposal — used for the audit transaction */
  performed_by: uuidSchema.optional().nullable(),
});

export type DeleteUnitsRequest = z.infer<typeof DeleteUnitsRequestSchema>;

export const DeleteUnitsResponseSchema = z.object({
  inventory_id:   uuidSchema,
  units_disposed: z.number().int().nonnegative(),
  tms_uuids:      z.array(z.string()),
  lot_status:     z.string(),
  lot_quantity:   z.number(),
});

export type DeleteUnitsResponse = z.infer<typeof DeleteUnitsResponseSchema>;
